// ==========================================
// Google Analytics & Microsoft Clarity
// ==========================================

export const loadGoogleAnalytics = () => {
  if (typeof window === 'undefined' || !window.gtag) return;
  window.gtag('consent', 'update', {
    'analytics_storage': 'granted'
  });
};

export const loadMicrosoftClarity = () => {
  if (typeof window === 'undefined') return;
  if (window.clarity) return;

  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.async = true;
  script.innerHTML = `
    (function(c,l,a,r,i,t,y){
      c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
      t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
      y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window, document, "clarity", "script", "s8ht16y102");
  `;
  document.head.appendChild(script);
};

export const removeAnalytics = () => {
  if (typeof window === 'undefined') return;

  if (window.gtag) {
    window.gtag('consent', 'update', {
      'analytics_storage': 'denied'
    });
  }

  if (window.clarity) {
    delete window.clarity;
  }

  document.cookie.split(";").forEach((c) => {
    const cookie = c.trim();
    if (cookie.startsWith('_ga') || cookie.startsWith('_gid') || cookie.startsWith('_clck') || cookie.startsWith('_clsk')) {
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
      document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
    }
  });
};

// ==========================================
// Meta CAPI Helper (fire-and-forget)
// ==========================================

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

function getCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match?.[2];
}

// Capture fbclid on landing so it persists across SPA navigations
function captureFbclid() {
  if (typeof window === 'undefined') return;
  try {
    const fbclid = new URL(window.location.href).searchParams.get('fbclid');
    if (fbclid) {
      sessionStorage.setItem('_fbclid', fbclid);
      sessionStorage.setItem('_fbclid_ts', Date.now().toString());
    }
  } catch {}
}
captureFbclid();

function getFbc(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  // 1. Cookie set by Meta pixel
  const cookieVal = getCookie('_fbc');
  if (cookieVal) return cookieVal;
  // 2. Current URL param
  try {
    const fbclid = new URL(window.location.href).searchParams.get('fbclid');
    if (fbclid) return `fb.1.${Date.now()}.${fbclid}`;
  } catch {}
  // 3. SessionStorage fallback (captured on landing)
  try {
    const stored = sessionStorage.getItem('_fbclid');
    const ts = sessionStorage.getItem('_fbclid_ts');
    if (stored && ts) return `fb.1.${ts}.${stored}`;
  } catch {}
  return undefined;
}

function sendCAPIEvent(eventName: string, eventId: string, customData?: Record<string, any>) {
  if (typeof window === 'undefined' || !SUPABASE_URL || !SUPABASE_KEY) return;
  const url = `${SUPABASE_URL}/functions/v1/meta-capi`;
  fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'apikey': SUPABASE_KEY,
    },
    body: JSON.stringify({
      event_name: eventName,
      event_id: eventId,
      event_source_url: window.location.href,
      user_data: {
        client_user_agent: navigator.userAgent,
        fbp: getCookie('_fbp'),
        fbc: getFbc(),
      },
      custom_data: customData,
    }),
  }).catch(() => {}); // fire-and-forget
}

// ==========================================
// Facebook Pixel Standard Events + CAPI
// ==========================================

export const trackViewContent = (params: {
  content_name: string;
  content_ids: string[];
  content_type: string;
  value: number;
  currency: string;
}) => {
  const eventId = crypto.randomUUID();
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', 'ViewContent', params, { eventID: eventId });
  }
  sendCAPIEvent('ViewContent', eventId, params);
};

export const trackAddToCart = (params: {
  content_name: string;
  content_ids: string[];
  content_type: string;
  value: number;
  currency: string;
  quantity?: number;
}) => {
  const eventId = crypto.randomUUID();
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', 'AddToCart', params, { eventID: eventId });
  }
  sendCAPIEvent('AddToCart', eventId, params);
};

export const trackInitiateCheckout = (params: {
  content_ids: string[];
  content_type: string;
  value: number;
  currency: string;
  num_items: number;
}) => {
  const eventId = crypto.randomUUID();
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', 'InitiateCheckout', params, { eventID: eventId });
  }
  sendCAPIEvent('InitiateCheckout', eventId, params);
};

export const trackAddPaymentInfo = (params?: {
  content_ids?: string[];
  content_type?: string;
  value?: number;
  currency?: string;
}) => {
  const eventId = crypto.randomUUID();
  const data = params || {};
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', 'AddPaymentInfo', data, { eventID: eventId });
  }
  sendCAPIEvent('AddPaymentInfo', eventId, data);
};

export const trackPurchase = (params: {
  content_ids: string[];
  content_type: string;
  value: number;
  currency: string;
  num_items?: number;
}, serverEventId?: string) => {
  // Use server event ID for deduplication if provided, otherwise generate one
  const eventId = serverEventId || crypto.randomUUID();
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', 'Purchase', params, { eventID: eventId });
  }
  // Only send browser CAPI if no server event ID (server already sent CAPI)
  if (!serverEventId) {
    sendCAPIEvent('Purchase', eventId, params);
  }
};

export const trackCompleteRegistration = (params?: {
  content_name?: string;
  status?: string;
  value?: number;
  currency?: string;
}) => {
  const eventId = crypto.randomUUID();
  const data = params || {};
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', 'CompleteRegistration', data, { eventID: eventId });
  }
  sendCAPIEvent('CompleteRegistration', eventId, data);
};

export const trackLead = (params?: {
  content_name?: string;
  content_category?: string;
  value?: number;
  currency?: string;
}) => {
  const eventId = crypto.randomUUID();
  const data = params || {};
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', 'Lead', data, { eventID: eventId });
  }
  sendCAPIEvent('Lead', eventId, data);
};

export const trackSearch = (params: {
  search_string: string;
  content_ids?: string[];
  content_category?: string;
}) => {
  const eventId = crypto.randomUUID();
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', 'Search', params, { eventID: eventId });
  }
  sendCAPIEvent('Search', eventId, params);
};

export const trackContact = () => {
  const eventId = crypto.randomUUID();
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', 'Contact', {}, { eventID: eventId });
  }
  sendCAPIEvent('Contact', eventId);
};

export const trackSubscribe = (params?: {
  value?: number;
  currency?: string;
  predicted_ltv?: number;
}) => {
  const eventId = crypto.randomUUID();
  const data = params || {};
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', 'Subscribe', data, { eventID: eventId });
  }
  sendCAPIEvent('Subscribe', eventId, data);
};

export const trackPageView = () => {
  const eventId = crypto.randomUUID();
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', 'PageView', {}, { eventID: eventId });
  }
  sendCAPIEvent('PageView', eventId);
};

// Type declarations for window object
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
    clarity?: (...args: any[]) => void;
    fbq?: (...args: any[]) => void;
  }
}
